/**
 * api/setting.ts —— 外观相关能力
 *
 * 这是第一批 API 里最直观的一组：改主题、改字号。
 * 它们同时是端到端验收 A 的主角（「调成黑夜模式」）。
 *
 * 注意 successText 与 run 写在同一对象里（设计文档 2.1.1）——
 * 这不是风格偏好，是信任原则的技术实现。改 run 的人必然看到 successText。
 */

import { settingsRepo, applyTheme, applyFontScale, FONT_SCALE_PRESETS } from '@/settings/settings';
import type { ThemeMode } from '@/types/models';
import type { ObjectDef } from '../registry';

/** 把主题模式转成给用户看的中文名 */
function themeLabel(mode: string): string {
  return { light: '浅色', dark: '黑夜', auto: '跟随系统' }[mode] ?? mode;
}

export const settingApi = {
  name: 'setting',
  desc: '应用的外观设置：主题与字号',
  userDesc: '调整外观 —— 深浅色主题、界面字号',
  methods: [
    {
      name: 'setTheme',
      desc: '切换界面主题。用户说「黑夜模式」「深色」用 dark；「白天」「浅色」用 light；「跟随系统」用 auto。',
      params: [
        {
          name: 'mode',
          type: 'enum' as const,
          values: ['light', 'dark', 'auto'],
          desc: '主题模式',
          required: true,
        },
      ],
      successText: (args: unknown[]) => {
        const mode = args[0] as ThemeMode;
        return `已切换到${themeLabel(mode)}主题`;
      },
      run: (args: unknown[]) => {
        const mode = args[0] as ThemeMode;
        settingsRepo.setApp({ theme: mode });
        const resolved = applyTheme(mode);
        // 立即生效但不广播 —— 求值器会返回 successText 作为唯一文案，
        // 避免「主题变了」和「提示语」两个来源打架
        return { value: resolved };
      },
    },
    {
      name: 'setFontScale',
      desc:
        '调整界面字号。可用档位：' +
        FONT_SCALE_PRESETS.map((p) => `${p.label}=${p.value}`).join('、') +
        '。用户说「字太小」就把 scale 调大一档。',
      params: [
        {
          name: 'scale',
          type: 'number' as const,
          min: 0.75,
          max: 2,
          desc: '字号倍数，1 为标准',
          required: true,
        },
      ],
      successText: (args: unknown[]) => {
        const scale = args[0] as number;
        const preset = FONT_SCALE_PRESETS.find((p) => p.value === scale);
        return preset ? `字号已调整为「${preset.label}」` : `字号已调整为 ${scale} 倍`;
      },
      run: (args: unknown[]) => {
        const scale = args[0] as number;
        settingsRepo.setApp({ fontScale: scale });
        applyFontScale(scale);
        return {};
      },
    },
    {
      name: 'getTheme',
      kind: 'query',
      desc: '读当前主题。用户问「现在是什么主题」时用这个。',
      params: [],
      successText: '已读取当前主题',
      run: () => {
        return { value: settingsRepo.getApp().theme };
      },
    },
  ],
} satisfies ObjectDef;
